import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, ScanLine, X } from 'lucide-react';
import Button from '../ui/Button';
import BarcodeScanner from '../shared/BarcodeScanner';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import harvestService from '../../services/harvest';
import { errorMessage } from '../../utils/apiResponse';
import { getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import {
  binPlace,
  loadLabel,
  type Bin,
  type HarvestLoad,
  type Packout,
  type PackoutGrade,
} from '../../types/harvest';
import { DateRangeInputs, Notice } from '../labour/shared';
import { useDateRange } from '../labour/labourUtils';
import { VoidModal } from './LoadsPanel';

const kgText = (n: number) => `${Math.round(n).toLocaleString()} kg`;

/**
 * Packouts, entered by hand from what the packhouse reports: kg tipped, kg per
 * grade, and which bins made it up. The tipped kg is shared across the bins by
 * their estimated weight and becomes their actual weight in every report.
 */
export default function PackoutsPanel({ startForLoad, onStarted }: { startForLoad: number | null; onStarted: () => void }) {
  const range = useDateRange();
  const [packouts, setPackouts] = useState<Packout[]>([]);
  const [editing, setEditing] = useState<{ packout: Packout | null; loadId: number | null } | null>(null);
  const [voiding, setVoiding] = useState<Packout | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPackouts(await harvestService.listPackouts({ from: range.from, to: range.to }));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load packouts'));
    }
  }, [range.from, range.to]);
  useEffect(() => { load(); }, [load]);

  // Opened from a load's "Enter packout".
  useEffect(() => {
    if (startForLoad) {
      setEditing({ packout: null, loadId: startForLoad });
      onStarted();
    }
  }, [startForLoad, onStarted]);

  const openExisting = async (p: Packout) => {
    try {
      setEditing({ packout: await harvestService.getPackout(p.id), loadId: p.load_id });
    } catch (e) {
      setError(errorMessage(e, 'Could not open the packout'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <DateRangeInputs {...range} />
        <Button size="sm" variant="primary" onClick={() => setEditing({ packout: null, loadId: null })}><Plus className="h-4 w-4" /> Enter packout</Button>
      </div>
      <p className="text-sm text-neutral-500">From the packhouse’s packout report: kg tipped, kg per grade, and the bins in it.</p>
      {error && <Notice tone="error">{error}</Notice>}
      {packouts.length === 0 ? (
        <p className="text-sm text-neutral-500 py-6 text-center">No packouts in this range.</p>
      ) : (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg" aria-label="Packouts">
          {packouts.map(p => (
            <li key={p.id} className="px-3 py-3 flex flex-wrap items-start gap-x-3 gap-y-2">
              <button type="button" onClick={() => openExisting(p)} className="flex-1 min-w-[12rem] text-left">
                <p className="text-sm font-medium text-neutral-900">
                  {p.packout_date} · {p.packhouse_name || 'Packhouse'}{p.reference ? ` · ${p.reference}` : ''}
                </p>
                <p className="text-xs text-neutral-500">
                  {kgText(p.total_kg_tipped)} tipped · {p.bin_count} bins
                  {p.load_number ? ` · load ${loadLabel({ season_year: p.load_season ?? 0, load_number: p.load_number })}` : ''}
                </p>
                <GradeBar grades={p.grades} total={p.total_kg_tipped} />
              </button>
              <button type="button" className="text-xs text-red-600 hover:underline" onClick={() => setVoiding(p)}>Void</button>
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <PackoutModal packout={editing.packout} loadId={editing.loadId} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {voiding && (
        <VoidModal title="Void packout" onClose={() => setVoiding(null)}
          onVoid={async reason => { await harvestService.voidPackout(voiding.id, reason); setVoiding(null); load(); }} />
      )}
    </div>
  );
}

const KIND_COLOUR: Record<string, string> = { saleable: 'bg-emerald-500', processing: 'bg-amber-400', waste: 'bg-neutral-400' };

function GradeBar({ grades, total }: { grades: Packout['grades']; total: number }) {
  if (!grades.length || !total) return null;
  return (
    <div className="mt-1.5 space-y-1">
      <div className="flex h-2 w-full max-w-sm overflow-hidden rounded-full bg-neutral-100" aria-hidden>
        {grades.map(g => <div key={g.grade_id} className={KIND_COLOUR[g.kind]} style={{ width: `${(g.kg / total) * 100}%`, opacity: 0.6 + 0.4 / (grades.indexOf(g) + 1) }} />)}
      </div>
      <p className="text-xs text-neutral-600">
        {grades.map(g => `${g.name} ${Math.round((g.kg / total) * 1000) / 10}%`).join(' · ')}
      </p>
    </div>
  );
}

function PackoutModal({ packout, loadId, onClose, onSaved }: {
  packout: Packout | null; loadId: number | null; onClose: () => void; onSaved: () => void;
}) {
  const tz = useOrgTimezone();
  const [date, setDate] = useState(packout?.packout_date ?? getTodayInTz(tz));
  const [packhouse, setPackhouse] = useState(packout?.packhouse_name ?? '');
  const [reference, setReference] = useState(packout?.reference ?? '');
  const [tipped, setTipped] = useState(packout ? String(packout.total_kg_tipped) : '');
  const [grades, setGrades] = useState<PackoutGrade[]>([]);
  const [gradeKg, setGradeKg] = useState<Record<number, string>>(
    Object.fromEntries((packout?.grades ?? []).map(g => [g.grade_id, String(g.kg)])),
  );
  const [load, setLoad] = useState<HarvestLoad | null>(null);
  const [bins, setBins] = useState<Map<number, Bin>>(new Map((packout?.bins ?? []).map(b => [b.id, b])));
  const [tagInput, setTagInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { harvestService.getGrades().then(setGrades).catch(() => {}); }, []);
  useEffect(() => {
    const id = loadId ?? packout?.load_id;
    if (!id) return;
    harvestService.getLoad(id).then(l => {
      setLoad(l);
      if (!packout) {
        setBins(new Map((l.bins ?? []).map(b => [b.id, b])));
        if (l.destination_name) setPackhouse(l.destination_name);
      }
    }).catch(() => {});
  }, [loadId, packout]);

  const addTag = async (code: string) => {
    try {
      const b = await harvestService.findByTag(code);
      if (!b) { setNote(`No bin with tag ${code}`); return; }
      setBins(m => new Map(m).set(b.id, b));
      setNote(`Added ${b.tag_code}`);
    } catch (e) {
      setNote(errorMessage(e, 'Not a valid tag'));
    }
  };

  const tippedKg = Number(tipped) || 0;
  const gradedKg = Object.values(gradeKg).reduce((a, v) => a + (Number(v) || 0), 0);
  const estKg = useMemo(() => [...bins.values()].reduce((a, b) => a + (b.est_net_kg ?? 0), 0), [bins]);
  const over = gradedKg > tippedKg * 1.001;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await harvestService.savePackout(packout?.id ?? null, {
        packout_date: date,
        packhouse_name: packhouse || null,
        reference: reference || null,
        total_kg_tipped: tippedKg,
        load_id: load?.id ?? null,
        grades: Object.entries(gradeKg).filter(([, v]) => v !== '' && Number(v) >= 0).map(([id, v]) => ({ grade_id: Number(id), kg: Number(v) })),
        bin_ids: [...bins.keys()],
      });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Could not save the packout'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title={packout ? 'Edit packout' : 'Enter packout'} onClose={onClose} onSubmit={submit} submitting={saving}
      submitDisabled={!tippedKg || !bins.size || over || !date} error={error}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Packout date" htmlFor="po-date">
          <input id="po-date" type="date" className={inputClass} value={date} onChange={e => setDate(e.target.value)} />
        </Field>
        <Field label="Packhouse reference" htmlFor="po-ref">
          <input id="po-ref" className={inputClass} value={reference} onChange={e => setReference(e.target.value)} />
        </Field>
      </div>
      <Field label="Packhouse" htmlFor="po-house">
        <input id="po-house" className={inputClass} value={packhouse} onChange={e => setPackhouse(e.target.value)} />
      </Field>
      <Field label="Total kg tipped" htmlFor="po-tipped">
        <input id="po-tipped" type="number" inputMode="decimal" min={0} className={inputClass} value={tipped} onChange={e => setTipped(e.target.value)} />
      </Field>

      <div>
        <p className="text-sm font-medium text-neutral-700 mb-1">Kg by grade</p>
        <div className="grid grid-cols-2 gap-2">
          {grades.map(g => (
            <label key={g.id} className="text-xs text-neutral-600">
              <span className="block mb-1">{g.name}{tippedKg && gradeKg[g.id] ? ` · ${Math.round((Number(gradeKg[g.id]) / tippedKg) * 1000) / 10}%` : ''}</span>
              <input type="number" inputMode="decimal" min={0} className={inputClass} value={gradeKg[g.id] ?? ''}
                aria-label={`${g.name} kg`} onChange={e => setGradeKg(m => ({ ...m, [g.id]: e.target.value }))} />
            </label>
          ))}
        </div>
        {tippedKg > 0 && (
          <p className={`text-xs mt-1 ${over ? 'text-red-700' : 'text-neutral-500'}`}>
            {over ? `Grades add up to ${kgText(gradedKg)} — more than was tipped.` : `${kgText(tippedKg - gradedKg)} not in a grade.`}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-neutral-700">
          Bins in this packout ({bins.size}){load ? ` · from load ${loadLabel(load)}` : ''}
        </p>
        <div className="flex flex-wrap gap-2">
          <input aria-label="Add a bin by tag" className={`${inputClass} w-36 font-mono`} placeholder="Tag, e.g. 4817-6" value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (tagInput.trim()) { addTag(tagInput.trim()); setTagInput(''); } } }} />
          <Button type="button" size="sm" variant="outline" disabled={!tagInput.trim()} onClick={() => { addTag(tagInput.trim()); setTagInput(''); }}>Add</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setScanning(true)}><ScanLine className="h-4 w-4" /> Scan</Button>
        </div>
        {note && <p className="text-xs text-neutral-600">{note}</p>}
        {bins.size > 0 && (
          <ul className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 max-h-48 overflow-y-auto text-sm">
            {[...bins.values()].map(b => (
              <li key={b.id} className="px-3 py-1.5 flex items-center gap-2">
                <span className="font-mono w-24">{b.tag_code ?? b.field_code}</span>
                <span className="flex-1 min-w-0 truncate text-neutral-600">{binPlace(b)}</span>
                <button type="button" aria-label={`Remove ${b.tag_code}`} onClick={() => setBins(m => { const n = new Map(m); n.delete(b.id); return n; })}
                  className="text-neutral-400 hover:text-red-600"><X className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
        {bins.size > 0 && tippedKg > 0 && (
          <p className="text-xs text-neutral-500">
            Estimated {kgText(estKg)} in these bins; {kgText(tippedKg)} tipped
            {estKg ? ` (${Math.round((tippedKg / estKg) * 100)}% of estimate)` : ''}. The tipped kg is shared across them by estimate.
          </p>
        )}
      </div>
      {scanning && <BarcodeScanner label="Scan bin tags" onScan={addTag} onClose={() => setScanning(false)} />}
    </FormModal>
  );
}
